"use client";
import React, { useEffect, useState } from "react";
import {
Search,
X,
ChevronsLeft,
ChevronsRight,
ChevronDown,
ChevronUp,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useAuditLogs } from "../../context/AuditLogContext";

export default function AuditLogs() {
    const { auditLogs, fetchAuditLogs, loading, error } = useAuditLogs();
    const [searchTerm, setSearchTerm] = useState("");
    const [filterModule, setFilterModule] = useState("All");
    const [filterAction, setFilterAction] = useState("All");
    const [expandedRows, setExpandedRows] = useState({});
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    useEffect(() => {
        fetchAuditLogs();
    }, []);

    // Unique modules
    const modules = ["All", ...new Set(auditLogs.map((log) => log.module).filter(Boolean)),];
    // Unique actions
    const actions = ["All", ...new Set(auditLogs.map((log) => log.action).filter(Boolean)),];

    const formatDate = (date) => {
        if (!date) return "";

        const parsedDate = new Date(date.replace(" ", "T"));

        if (Number.isNaN(parsedDate.getTime())) {
            return date;
        }

        return parsedDate.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    // Filtering
    const filteredLogs = auditLogs.filter((log) => {
        const search = searchTerm.toLowerCase().trim();
        const matchesModule = filterModule === "All" || log.module === filterModule;
        const matchesAction = filterAction === "All" || log.action === filterAction;
        const rawCreatedAt = log.created_at ? String(log.created_at).toLowerCase() : "";
        const formattedCreatedAt = log.created_at ? formatDate(log.created_at).toLowerCase() : "";

        const matchesSearch =
            log.id?.toString().toLowerCase().includes(search) ||
            log.module?.toLowerCase().includes(search) ||
            log.action?.toLowerCase().includes(search) ||
            log.user_id?.toString().toLowerCase().includes(search) ||
            log.api_url?.toLowerCase().includes(search) ||
            log.ip_address?.toLowerCase().includes(search) ||
            rawCreatedAt.includes(search) ||
            formattedCreatedAt.includes(search);

            return matchesModule && matchesAction && matchesSearch;
    });

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / rowsPerPage));
    const indexOfLastRow = page * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredLogs.slice(indexOfFirstRow, indexOfLastRow);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [searchTerm, filterModule, filterAction, rowsPerPage]);

    const toggleRow = (id) => {
        setExpandedRows((prev) => ({...prev, [id]: !prev[id],}));
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterModule("All");
        setFilterAction("All");
        setPage(1);
    };

    const formatJson = (value) => {
        if (!value) return "-";
        try {
            const parsed = typeof value === "string" ? JSON.parse(value) : value;
            return JSON.stringify(parsed, null, 2);
        } catch {
        return String(value);
        }
    };

    return (
    <> 
        <div className="bg-sky-50 min-h-screen"> 
            <div className="px-6 py-6 sm:px-9 max-sm:px-4">

                {/* Header */}
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
                    Audit Logs
                </h1>

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">

                    {/* Search + Filters */}
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 w-full">

                        {/* Search */}
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white w-full lg:max-w-md focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                            <Search size={16} className="text-gray-400" />

                            <input
                            type="text"
                            placeholder="Search audit logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="outline-none text-sm flex-1 bg-transparent w-full"
                            />
                        </div>

                        {/* Module Filter */}
                        <select
                            value={filterModule}
                            onChange={(e) => setFilterModule(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none bg-white focus:ring-2 focus:ring-[#8c57ff]/40"
                        >
                            {modules.map((module) => (
                            <option key={module} value={module}>
                                {module === "All"
                                ? "All Modules"
                                : module}
                            </option>
                            ))}
                        </select>

                        {/* Action Filter */}
                        <select
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none bg-white focus:ring-2 focus:ring-[#8c57ff]/40"
                        >
                            {actions.map((action) => (
                            <option key={action} value={action}>
                                {action === "All"
                                ? "All Actions"
                                : action}
                            </option>
                            ))}
                        </select>

                        {/* Clear */}
                        {(searchTerm ||
                            filterModule !== "All" ||
                            filterAction !== "All") && (
                            <button
                            type="button"
                            onClick={clearFilters}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap"
                            >
                            <X size={14} />
                            Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                    <div className="overflow-x-auto">

                        <table className="min-w-[1400px] w-full border-collapse">
                            <thead>
                                <tr className="bg-sky-100 text-gray-700">

                                    <th className="py-3 px-4 text-left font-semibold w-[5%]">
                                    ID
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[12%]">
                                    Module
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[20%]">
                                    API URL
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[10%]">
                                    Action
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[8%]">
                                    User ID
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[12%]">
                                    IP Address
                                    </th>

                                    <th className="py-3 px-4 text-left font-semibold w-[15%]">
                                    Created At
                                    </th>

                                    <th className="py-3 px-4 text-center font-semibold w-[8%]">
                                    Details
                                    </th>

                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center py-8 text-gray-500"
                                        >
                                            Loading audit logs...
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center py-8 text-red-500"
                                        >
                                            {error}
                                        </td>
                                    </tr>
                                ) : currentRows.length > 0 ? (
                                    currentRows.map((log, index) => (
                                    <React.Fragment key={log.id || index}>

                                        <tr
                                        className={`border-b text-sm hover:bg-gray-50 transition ${
                                            index % 2 === 1
                                            ? "bg-[#f7f6fe]"
                                            : ""
                                        }`}
                                        >
                                            {/* ID */}
                                            <td className="py-3 px-4 text-indigo-600">
                                                {indexOfFirstRow + index + 1}
                                            </td>

                                            {/* Module */}
                                            <td className="py-3 px-4 text-gray-800">
                                                <span className="font-medium">
                                                {log.module || "-"}
                                                </span>
                                            </td>

                                            {/* API URL */}
                                            <td className="py-3 px-4">
                                                <p
                                                className="text-gray-700 max-w-[350px] truncate"
                                                title={log.api_url || ""}
                                                >
                                                {log.api_url || "-"}
                                                </p>
                                            </td>

                                            {/* Action */}
                                            <td className="py-3 px-4">
                                                <span
                                                className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                    log.action
                                                    ?.toLowerCase()
                                                    .includes("delete")
                                                    ? "bg-red-100 text-red-700"
                                                    : log.action
                                                        ?.toLowerCase()
                                                        .includes("update")
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : log.action
                                                        ?.toLowerCase()
                                                        .includes("create")
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-blue-100 text-blue-700"
                                                }`}
                                                >
                                                {log.action || "-"}
                                                </span>
                                            </td>

                                            {/* User ID */}
                                            <td className="py-3 px-4 text-gray-700">
                                                {log.user_id || "-"}
                                            </td>

                                            {/* IP */}
                                            <td className="py-3 px-4 text-gray-700">
                                                {log.ip_address || "-"}
                                            </td>

                                            {/* Created */}
                                            <td className="py-3 px-4 text-gray-700">
                                                {formatDate(log.created_at)}
                                            </td>

                                            {/* Details */}
                                            <td className="py-3 px-4 text-center">
                                                <button
                                                onClick={() => toggleRow(log.id)}
                                                className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                                title="View details"
                                                >
                                                {expandedRows[log.id] ? (
                                                    <ChevronUp size={17} />
                                                ) : (
                                                    <ChevronDown size={17} />
                                                )}
                                                </button>
                                            </td>
                                        </tr>

                                        {/* Expanded Details */}
                                        {expandedRows[log.id] && (
                                        <tr className="bg-gray-50 border-b">
                                            <td
                                            colSpan="8"
                                            className="px-6 py-5"
                                            >
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                                                    {/* Old Data */}
                                                    {/* <div>
                                                        <h4 className="font-semibold text-gray-700 mb-2">
                                                            Old Data
                                                        </h4>

                                                        <pre className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-700 overflow-auto max-h-60 whitespace-pre-wrap">
                                                            {formatJson(log.old_data)}
                                                        </pre>
                                                    </div> */}

                                                    {/* New Data */}
                                                    {/* <div>
                                                        <h4 className="font-semibold text-gray-700 mb-2">
                                                            New Data
                                                        </h4>

                                                        <pre className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-700 overflow-auto max-h-60 whitespace-pre-wrap">
                                                            {formatJson(log.new_data)}
                                                        </pre>
                                                    </div> */}

                                                    {/* User Agent */}
                                                    <div>
                                                        <h4 className="font-semibold text-gray-700 mb-2">
                                                            User Agent
                                                        </h4>

                                                        <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-600 break-words">
                                                            {log.user_agent || "-"}
                                                        </div>
                                                    </div>

                                                    {/* Data */}
                                                    <div>
                                                        <h4 className="font-semibold text-gray-700 mb-2">
                                                            Data
                                                        </h4>

                                                        <pre className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-700 overflow-auto max-h-60 whitespace-pre-wrap">
                                                            {formatJson(log.data)}
                                                        </pre>
                                                    </div>

                                                </div>
                                            </td>
                                        </tr>
                                        )}
                                    </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center py-8 text-gray-500"
                                        >
                                            No audit logs found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">

                        {/* Rows Per Page */}
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                            <span>Rows per page:</span>

                            <div className="border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40">
                                <select
                                    className="px-2 py-1 outline-none bg-white"
                                    value={rowsPerPage}
                                    onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(1);
                                    }}
                                >
                                    {[5, 10, 15, 25, 50].map((num) => (
                                    <option key={num} value={num}>
                                        {num}
                                    </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Pagination Buttons */}
                        <div className="flex items-center space-x-1">

                            <button
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                <ChevronsLeft size={20} />
                            </button>

                            <button
                                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                onClick={() =>
                                    setPage((prev) =>
                                    Math.max(prev - 1, 1)
                                    )
                                }
                                disabled={page === 1}
                                >
                                <MdKeyboardArrowLeft size={20} />
                            </button>

                            {(() => {
                            let startPage = Math.max(page - 1, 1);
                            let endPage = startPage + 2;

                            if (endPage > totalPages) {
                                endPage = totalPages;
                                startPage = Math.max(
                                endPage - 2,
                                1
                                );
                            }

                            const pages = [];

                            for (
                                let i = startPage;
                                i <= endPage;
                                i++
                            ) {
                                pages.push(
                                <button
                                    key={i}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    i === page
                                        ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                    }`}
                                    onClick={() => setPage(i)}
                                >
                                    {i}
                                </button>
                                );
                            }

                            return pages;
                            })()}

                            <button
                                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                onClick={() =>
                                    setPage((prev) =>
                                    Math.min(
                                        prev + 1,
                                        totalPages
                                    )
                                    )
                                }
                                disabled={page === totalPages}
                                >
                                <MdKeyboardArrowRight size={20} />
                            </button>

                            <button
                                onClick={() => setPage(totalPages)}
                                disabled={page === totalPages}
                                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                <ChevronsRight size={20} />
                            </button>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>

    );
}
